import type {
  RevisionObjectTooltip,
  RevisionOverlayShape,
} from 'features/knowledgeDocumentViewer/components/revisionCompare/revisionDiffModel'
import { impactSeverity } from 'features/knowledgeDocumentViewer/components/revisionCompare/revisionImpactSeverity'
import { OverlaySvg } from 'features/knowledgeDocumentViewer/components/viewer/shared/styles'
import type {
  RevisionDiffDetailBox,
  RevisionObjectStatus,
} from 'features/knowledgeDocumentViewer/hooks/useRevisionDiffDetails'
import type { CSSProperties, MouseEvent as ReactMouseEvent } from 'react'
import { forwardRef } from 'react'
import styled, { keyframes } from 'styled-components'

export const CHANGE_STATUS_COLORS: Record<
  RevisionObjectStatus,
  { fill: string; stroke: string; dashed?: boolean }
> = {
  added: { stroke: '#1f9d55', fill: 'rgba(31, 157, 85, 0.26)' },
  removed: { stroke: '#d9363e', fill: 'rgba(217, 54, 62, 0.26)' },
  changed: { stroke: '#e8890c', fill: 'rgba(232, 137, 12, 0.26)' },
  matched: {
    stroke: '#9aa6b8',
    fill: 'rgba(154, 166, 184, 0.06)',
    dashed: true,
  },
}

/** Emphasis style for the object(s) of the currently-selected event. */
const SELECTED_BOX_STYLE = {
  stroke: '#4b82ff',
  fill: 'rgba(75, 130, 255, 0.22)',
}

/** Receding look for layout-only changes so substantive ones stand out. */
const MUTED_CHANGED_STYLE = {
  fill: 'rgba(232, 137, 12, 0.08)',
  strokeOpacity: 0.55,
}

const EMPTY_SELECTION: ReadonlySet<string> = new Set()

const regionFlash = keyframes`
  0% { stroke-opacity: 1; fill-opacity: 0.22; }
  50% { stroke-opacity: 0.35; fill-opacity: 0.08; }
  100% { stroke-opacity: 1; fill-opacity: 0.18; }
`

const RegionHighlight = styled.rect`
  pointer-events: none;
  animation: ${regionFlash} 0.9s ease-in-out 2;
`

type RevisionDiffOverlayProps = {
  shapes: readonly RevisionOverlayShape[]
  imageNatural: { w: number; h: number }
  selectedObjectIds?: ReadonlySet<string>
  selectedRegion?: RevisionDiffDetailBox | null
  onShapeHover?: (tooltip: RevisionObjectTooltip, e: ReactMouseEvent) => void
  onShapeHoverEnd?: () => void
  onShapeClick?: (objectId: string) => void
}

const screenPx = (base: number) => `calc(${base} * var(--flow-image-unit, 1px))`

const DASH_ARRAY = `${screenPx(5)} ${screenPx(4)}`

export const RevisionDiffOverlay = forwardRef<
  SVGSVGElement,
  RevisionDiffOverlayProps
>(function RevisionDiffOverlay(
  {
    shapes,
    imageNatural,
    selectedObjectIds = EMPTY_SELECTION,
    selectedRegion,
    onShapeHover,
    onShapeHoverEnd,
    onShapeClick,
  },
  ref
) {
  if (imageNatural.w <= 0 || imageNatural.h <= 0) return null
  const { w, h } = imageNatural

  const unselected = shapes.filter(
    (shape) => !selectedObjectIds.has(shape.objectId)
  )
  const selectedShapes = shapes.filter((shape) =>
    selectedObjectIds.has(shape.objectId)
  )

  return (
    <OverlaySvg
      ref={ref}
      role='presentation'
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio='none'
      style={{ pointerEvents: 'none' }}>
      {[...unselected, ...selectedShapes].map((shape) => {
        const colors = CHANGE_STATUS_COLORS[shape.status]
        const selected = selectedObjectIds.has(shape.objectId)
        const stroke = selected ? SELECTED_BOX_STYLE.stroke : colors.stroke
        const muted =
          !selected &&
          shape.status === 'changed' &&
          impactSeverity(shape.tooltip.impact) === 'minor'
        let fill = colors.fill
        if (selected) fill = SELECTED_BOX_STYLE.fill
        else if (muted) fill = MUTED_CHANGED_STYLE.fill
        const strokeOpacity = muted ? MUTED_CHANGED_STYLE.strokeOpacity : 1
        const strokeScale = muted ? 0.6 : 1
        const dashed = !selected && colors.dashed
        const dashStyle: CSSProperties = dashed
          ? { strokeDasharray: DASH_ARRAY }
          : {}
        const hasHalo = shape.status !== 'matched' && !muted
        const handlers = {
          onMouseEnter: (e: ReactMouseEvent) =>
            onShapeHover?.(shape.tooltip, e),
          onMouseMove: (e: ReactMouseEvent) => onShapeHover?.(shape.tooltip, e),
          onMouseLeave: () => onShapeHoverEnd?.(),
          onClick: () => onShapeClick?.(shape.objectId),
        }
        return shape.primitives.map((primitive, i) => {
          const key = `${shape.id}-${i}`
          if (primitive.kind === 'rect') {
            const geometry = {
              x: primitive.x0 * w,
              y: primitive.y0 * h,
              width: (primitive.x1 - primitive.x0) * w,
              height: (primitive.y1 - primitive.y0) * h,
              rx: 2,
              ry: 2,
            }
            return (
              <g key={key} data-muted={muted ? 'true' : undefined}>
                {hasHalo ? (
                  <rect
                    {...geometry}
                    fill='none'
                    stroke={stroke}
                    strokeOpacity={0.2}
                    style={{
                      pointerEvents: 'none',
                      strokeWidth: screenPx(5),
                    }}
                  />
                ) : null}
                <rect
                  {...geometry}
                  fill={fill}
                  stroke={stroke}
                  strokeOpacity={strokeOpacity}
                  style={{
                    pointerEvents: 'visiblePainted',
                    cursor: 'pointer',
                    strokeWidth: screenPx(selected ? 4 : 2 * strokeScale),
                    ...dashStyle,
                  }}
                  {...handlers}
                />
              </g>
            )
          }
          const points = primitive.points
            .map(([x, y]) => `${x * w},${y * h}`)
            .join(' ')
          const lineProps = {
            points,
            fill: 'none',
            strokeLinecap: 'round' as const,
            strokeLinejoin: 'round' as const,
          }
          return (
            <g key={key} data-muted={muted ? 'true' : undefined}>
              {hasHalo ? (
                <polyline
                  {...lineProps}
                  stroke={stroke}
                  strokeOpacity={0.2}
                  style={{
                    pointerEvents: 'none',
                    strokeWidth: screenPx(6),
                  }}
                />
              ) : null}
              <polyline
                {...lineProps}
                stroke={stroke}
                strokeOpacity={strokeOpacity}
                style={{
                  pointerEvents: 'none',
                  strokeWidth: screenPx(selected ? 6 : 4 * strokeScale),
                  ...dashStyle,
                }}
              />
              <polyline
                {...lineProps}
                stroke='transparent'
                style={{
                  pointerEvents: 'stroke',
                  cursor: 'pointer',
                  strokeWidth: screenPx(14),
                }}
                {...handlers}
              />
            </g>
          )
        })
      })}
      {selectedRegion ? (
        <RegionHighlight
          key={`${selectedRegion.x0}-${selectedRegion.y0}-${selectedRegion.x1}-${selectedRegion.y1}`}
          x={selectedRegion.x0 * w}
          y={selectedRegion.y0 * h}
          width={(selectedRegion.x1 - selectedRegion.x0) * w}
          height={(selectedRegion.y1 - selectedRegion.y0) * h}
          rx={2}
          ry={2}
          fill={SELECTED_BOX_STYLE.fill}
          stroke={SELECTED_BOX_STYLE.stroke}
          style={{ strokeWidth: screenPx(6) }}
        />
      ) : null}
    </OverlaySvg>
  )
})
